import { Num, T, useGT } from 'gt-react';
import { FileZipIcon, XIcon } from '@phosphor-icons/react';
import { MAX_VENDOR_PORTAL_FILES_PER_ARCHIVE } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import type { StartedVendorPortalDownload } from './useVendorPortalFileActions';

/**
 * Download every file the filters match, as one zip. When more match than one zip takes,
 * download the page instead: the user pages through and downloads each page in turn.
 */
export function VendorPortalDownloadAllButton({
  total,
  pageFileCount,
  disabled,
  onDownloadAll,
  onDownloadPage,
}: {
  total: number;
  /** Files on the current page; never more than one zip takes. */
  pageFileCount: number;
  /** While the count is still the previous query's. */
  disabled: boolean;
  onDownloadAll: () => void;
  onDownloadPage: () => void;
}) {
  const gt = useGT();
  if (total <= MAX_VENDOR_PORTAL_FILES_PER_ARCHIVE) {
    return (
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || total === 0}
        onClick={onDownloadAll}
      >
        <FileZipIcon className="h-4 w-4" />
        <T context="Vendor portals button that downloads every file matching the filters as one zip">
          Download All (<Num>{total}</Num>)
        </T>
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={disabled || pageFileCount === 0}
      title={gt(
        'One zip holds up to {max} files. Download this page, then use Next for the following files. Starting the next download does not cancel this one.',
        {
          context:
            'Tooltip on the vendor portals Download Page button when more files match than one zip holds',
          max: MAX_VENDOR_PORTAL_FILES_PER_ARCHIVE,
        },
      )}
      onClick={onDownloadPage}
    >
      <FileZipIcon className="h-4 w-4" />
      <T context="Vendor portals button that downloads every file on the current page as one zip">
        Download This Page (<Num>{pageFileCount}</Num>)
      </T>
    </Button>
  );
}

/** Shows the page is narrowed to one workflow run, and clears that. */
export function VendorPortalRunFilterChip({
  workflowRunId,
  onClear,
}: {
  workflowRunId: string | undefined;
  onClear: () => void;
}) {
  const gt = useGT();
  if (!workflowRunId) return null;
  return (
    <span className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-sm">
      <T context="Vendor portals filter chip when the list shows one workflow run's files">
        One run only
      </T>
      <button
        type="button"
        onClick={onClear}
        aria-label={gt('Show files from every run', {
          context: 'Vendor portals button that clears the one-run filter',
        })}
        className="text-muted-foreground hover:text-foreground"
      >
        <XIcon className="h-3 w-3" />
      </button>
    </span>
  );
}

/**
 * The zips started from this page, each named by the files it holds, so the user can page
 * on and keep downloading without waiting and still see what each click took.
 */
export function VendorPortalStartedDownloads({
  downloads,
}: {
  downloads: StartedVendorPortalDownload[];
}) {
  const gt = useGT();
  if (downloads.length === 0) return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-1 px-1 text-sm text-muted-foreground"
    >
      <T context="Vendor portals label before the list of zip downloads the user started; the browser's download manager shows their progress">
        Zips started (your browser shows their progress):
      </T>
      {downloads.map((download) => (
        <span
          key={download.id}
          className="rounded-md border px-2 py-0.5 text-xs text-foreground"
          title={
            download.missingFileCount > 0
              ? gt(
                  '{missingFileCount, plural, one {# file is no longer in storage; the zip lists it in MISSING-FILES.txt.} other {# files are no longer in storage; the zip lists them in MISSING-FILES.txt.}}',
                  {
                    context:
                      'Tooltip on a started vendor portal zip download when some of its files are missing from storage',
                    missingFileCount: download.missingFileCount,
                  },
                )
              : undefined
          }
        >
          {startedDownloadLabel(download, gt)}
        </span>
      ))}
    </div>
  );
}

/** "Files 51–100" for a page's zip, "3 files" otherwise, and how many are missing. */
function startedDownloadLabel(
  download: StartedVendorPortalDownload,
  gt: ReturnType<typeof useGT>,
): string {
  const files = download.fileRange
    ? gt('Files {first}–{last}', {
        context:
          'Vendor portals started zip download of one page, named by the rows of the list it holds',
        first: download.fileRange.first,
        last: download.fileRange.last,
      })
    : gt('{fileCount, plural, one {# file} other {# files}}', {
        context:
          'Vendor portals started zip download of a selection or of every match, named by its file count',
        fileCount: download.fileCount,
      });
  if (download.missingFileCount === 0) return files;
  return gt('{files} ({missingFileCount} missing)', {
    context:
      'Vendor portals started zip download that lacks files storage no longer has',
    files,
    missingFileCount: download.missingFileCount,
  });
}
